import { useState } from 'react';
import { useLazyQuery } from '@apollo/client';

export interface FileUploadProps {
  client?: any;
  query: any;
  params?: any;
  shouldSendFileSize?: boolean;
}

export type PresignedUrlResponse = {
  preSignedUrl: string;
  assetKey: string;
  id: number;
};

export const useFileUpload = ({ query, shouldSendFileSize = true }: FileUploadProps) => {
  const [progress, setProgress] = useState<number>(0);
  const [lazyQuery] = useLazyQuery(query);

  const getUploadPresignedUrl = async (
    file: File,
    params: Record<string, string | number | boolean>
  ): Promise<PresignedUrlResponse> => {
    try {
      const response = await lazyQuery({
        variables: {
          params: {
            ...params,
            fileName: file.name,
            ...(shouldSendFileSize ? { fileSize: file.size } : {}),
          },
        },
      });

      return Object.values(response.data)[0] as PresignedUrlResponse;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const uploadFile = async (file: File, params: Record<string, string | number | boolean>) => {
    if (!file) return;

    setProgress(0);

    const { preSignedUrl, assetKey, id } = await getUploadPresignedUrl(file, params);

    return new Promise((res, rej) => {
      const xhr = new XMLHttpRequest();
      xhr.withCredentials = false;

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          setProgress(Math.round((event.loaded * 100) / event.total));
        }
      };
      xhr.onreadystatechange = async function () {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            res({ preSignedUrl, assetKey, id });
          } else {
            rej('Error uploading file');
          }
        }
      };
      xhr.open('PUT', preSignedUrl);
      xhr.send(file);
    });
  };

  return { getUploadPresignedUrl, uploadFile, progress };
};
